import type { ColorAttribute } from '../helpers';
import type { StoreOption, VariantMappingRepairStrategy } from './types';

export const sameCaseInsensitive = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

// Shared with positional matching so every strategy uses the same casing rules.
export const findOptionsByLabelIgnoringCase = (label: string, options: readonly StoreOption[]): StoreOption[] =>
    options.filter((option) => sameCaseInsensitive(option.label, label));

export const findOptionIgnoringCase = (attribute: ColorAttribute, options: readonly StoreOption[], requireSameID = true): StoreOption | null => {
    const matches = findOptionsByLabelIgnoringCase(attribute.Label, options).filter(
        (option) => !requireSameID || sameCaseInsensitive(option.optionID, attribute.Id)
    );
    return matches.length === 1 ? matches[0] : null;
};

export const findValueIgnoringCase = (value: ColorAttribute['Value'], values: StoreOption['values']): StoreOption['values'][number] | null => {
    const exactMatches = values.filter((candidate) => candidate.valueID === value.Id);
    const matches = exactMatches.length ? exactMatches : values.filter((candidate) => !!value.Id && sameCaseInsensitive(candidate.valueID, value.Id));
    return matches.length === 1 && sameCaseInsensitive(matches[0].label, value.Label) ? matches[0] : null;
};

const repairAttributeCasing = (attribute: ColorAttribute, options: readonly StoreOption[]): ColorAttribute | null => {
    const option = findOptionIgnoringCase(attribute, options);
    const value = option && findValueIgnoringCase(attribute.Value, option.values);
    return option && value
        ? { ...attribute, Id: option.optionID, Label: option.label, Value: { ...attribute.Value, Id: value.valueID, Label: value.label } }
        : null;
};

export const casingStrategy: VariantMappingRepairStrategy = {
    id: 'casing',
    isApplicable: () => true,
    repair: (attributes, { options }) => {
        const repaired = attributes.map((attribute) => repairAttributeCasing(attribute, options));
        return repaired.every((attribute) => attribute !== null) ? repaired : null;
    }
};
